import React, { useState, useCallback } from "react";
import {
  Container,
  Typography,
  Box,
  Button,
  TextField,
  InputAdornment,
  Divider,
  Chip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { AddCircleOutlineOutlined } from "@mui/icons-material";
import { Search } from "lucide-react";
import Sidebar from "../components/Admin/Sidebar/Sidebar";
import Header from "../components/Admin/header/Header";
import { EditIcon } from "../comps/CustomIcons";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";
import {
  DEFAULT_LIMIT,
  DEFAULT_ROWS_PER_PAGE_OPTIONS,
} from "../utils/constants";
import { getCategoryApi } from "../services/category.service";
import { useGetApi } from "../hooks/useGetApi";
import Loader from "../components/Loader/loader";
import ErrorMessage from "../components/ErrorMessage/errorMessage";
import AddSubCategoryModal from "../components/item_master/modal/sub-category-modal";

export default function MasterSubCategory() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [search, setSearch] = useState("");
  const [searchBy, setSearchBy] = useState("categoryName");
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: DEFAULT_LIMIT,
  });
  const [sortModel, setSortModel] = useState([{ field: "categoryName", sort: "asc" }]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const searchByOptions = [
    { value: "categoryName", label: "Sub Category Name" },
    { value: "code", label: "Code" },
  ];
  const searchByLabel =
    searchByOptions.find((o) => o.value === searchBy)?.label || "Sub Category Name";

  const handleModalOpen = () => setModalOpen(true);
  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedRow(null);
  };

  const handleEdit = (rowData) => {
    const original = rowData._original || rowData;
    setSelectedRow(original);
    handleModalOpen();
  };

  const handleSortModelChange = (newSortModel) => setSortModel(newSortModel);
  const handlePaginationModelChange = (newModel) => setPaginationModel(newModel);

  const getColumns = useCallback(() => {
    return [
      {
        field: "code",
        headerName: "Code",
        flex: 1,
        minWidth: 100,
        headerAlign: "left",
        align: "left",
        sortable: true,
      },
      {
        field: "categoryName",
        headerName: "Sub Category Name",
        flex: 1,
        minWidth: 140,
        headerAlign: "left",
        align: "left",
        sortable: true,
      },
      {
        field: "parentCategoryName",
        headerName: "Category Name",
        flex: 1,
        minWidth: 140,
        headerAlign: "left",
        align: "left",
        sortable: true,
      },
      {
        field: "status",
        headerName: "Status",
        flex: 0.6,
        minWidth: 90,
        headerAlign: "center",
        align: "center",
        sortable: true,
        renderCell: (params) => (
          <Chip
            label={params.value || "Active"}
            color={params.value === "Inactive" ? "error" : "success"}
            size="small"
            variant="outlined"
          />
        ),
      },
      {
        field: "actions",
        headerName: "Actions",
        flex: 0,
        width: 70,
        minWidth: 60,
        headerAlign: "center",
        align: "center",
        sortable: false,
        renderCell: (params) => (
          <EditIcon
            sx={{ color: "transparent", fontSize: "18px", cursor: "pointer" }}
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(params.row._original || params.row);
            }}
          />
        ),
      },
    ];
  }, []);

  const {
    dataList: categoryList,
    dataCount: categoryCount,
    isLoading,
    isError,
    refetch,
  } = useGetApi({
    apiFunction: getCategoryApi,
    params: {
      limit: paginationModel.pageSize,
      page: paginationModel.page + 1,
      search,
      searchBy,
      sort: sortModel[0]?.field || "categoryName",
      order: sortModel[0]?.sort || "asc",
      type: "subcategory",
    },
    dependencies: [search, searchBy, paginationModel.pageSize, paginationModel.page, sortModel],
    debounceDelay: 500,
    dataKey: "categories",
    countKey: "total",
  });

  const rawRows =
    categoryList?.map((row) => ({
      id: row._id,
      _id: row._id,
      code: row?.code || "-",
      categoryName: row?.categoryName || "-",
      parentCategoryName: row?.parentCategory?.categoryName || "-",
      status: row?.active ? "Active" : "Inactive",
      _original: row,
    })) || [];

  const sortField = sortModel[0]?.field || "categoryName";
  const sortOrder = sortModel[0]?.sort || "asc";
  const sortedRows = [...rawRows].sort((a, b) => {
    const aVal = a[sortField] ?? "";
    const bVal = b[sortField] ?? "";
    const cmp = String(aVal).localeCompare(String(bVal), undefined, { numeric: true });
    return sortOrder === "asc" ? cmp : -cmp;
  });

  const pageSize = paginationModel.pageSize;
  const page = paginationModel.page;
  const rows =
    sortedRows.length > pageSize
      ? sortedRows.slice(page * pageSize, page * pageSize + pageSize)
      : sortedRows;

  const dataGridSx = {
    border: "none",
    "& .MuiDataGrid-root": { border: "none", width: "100%" },
    "& .MuiDataGrid-main": { width: "100%" },
    "& .MuiDataGrid-cell": {
      borderBottom: "1px solid #f0f0f0",
      padding: isMobile ? "4px" : "8px",
      fontSize: isMobile ? "12px" : "14px",
    },
    "& .MuiDataGrid-columnHeaders": {
      backgroundColor: "#f5f5f5",
      borderBottom: "2px solid #e0e0e0",
    },
    "& .MuiDataGrid-columnHeader": {
      backgroundColor: "#f5f5f5",
      fontWeight: "bold",
      fontSize: isMobile ? "12px" : "14px",
    },
    "& .MuiDataGrid-columnHeaderTitle": { fontWeight: "bold", fontSize: isMobile ? "12px" : "14px" },
    "& .MuiDataGrid-row": {
      "&:hover": { backgroundColor: "#f5f5f5", cursor: "pointer" },
      "&:nth-of-type(odd)": { backgroundColor: "#fafafa" },
      "&:nth-of-type(even)": { backgroundColor: "white" },
    },
    "& .MuiDataGrid-footerContainer": { borderTop: "2px solid #e0e0e0", backgroundColor: "#f9f9f9" },
    "& .MuiDataGrid-virtualScroller": { minHeight: "200px" },
    [theme.breakpoints.down("sm")]: {
      "& .MuiDataGrid-columnHeader": { fontSize: "11px", padding: "4px" },
      "& .MuiDataGrid-cell": { fontSize: "11px", padding: "2px 4px" },
    },
  };

  return (
    <Container maxWidth={false} disableGutters>
      <Header />
      <Box sx={{ display: "flex" }}>
        <Box>
          <Sidebar />
        </Box>
        <Box sx={{ width: "100%", pr: "90px", paddingBottom: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{
              color: "#5D6A82",
              textTransform: "none",
              fontSize: "16px",
              fontWeight: "bold",
              padding: 0,
              transition: "none !important",
              mt: 2,
            }}
          >
            Back
          </Button>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 2 }}>
            <Typography sx={{ color: "rgba(93, 106, 130, 1)", fontSize: 20 }}>
              Sub Category Master
            </Typography>
          </Box>
          <Box
            sx={{
              my: "20px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              paddingBottom: 2,
            }}
          >
            <TextField
              placeholder={`Search by ${searchByLabel}`}
              size="small"
              sx={{ width: "300px" }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <select
                        className="uid-select"
                        value={searchBy}
                        onChange={(e) => setSearchBy(e.target.value)}
                        style={{
                          border: "none",
                          outline: "none",
                          background: "transparent",
                          cursor: "pointer",
                          color: "#333",
                          fontWeight: "500",
                        }}
                      >
                        {searchByOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                      <Search />
                    </InputAdornment>
                  ),
                },
              }}
              value={search || ""}
              onChange={(e) => setSearch(e.target.value || "")}
            />
            <Button
              variant="contained"
              sx={{ textTransform: "none", display: "flex", alignItems: "center", gap: 0.5 }}
              onClick={handleModalOpen}
            >
              <AddCircleOutlineOutlined />
              <Typography sx={{ color: "white" }}>New Sub Category</Typography>
            </Button>
          </Box>

          {isLoading ? (
            <Loader />
          ) : isError ? (
            <ErrorMessage />
          ) : (
            <Box
              sx={{
                height: { xs: "calc(100vh - 300px)", sm: "calc(100vh - 320px)", md: "calc(100vh - 350px)" },
                width: "100%",
                minHeight: "300px",
                maxWidth: "100%",
                overflow: "hidden",
              }}
            >
              <DataGrid
                rows={rows}
                columns={getColumns()}
                paginationModel={paginationModel}
                onPaginationModelChange={handlePaginationModelChange}
                pageSizeOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
                paginationMode="server"
                rowCount={categoryCount}
                sortingMode="server"
                sortModel={sortModel}
                onSortModelChange={handleSortModelChange}
                disableRowSelectionOnClick
                loading={isLoading}
                sx={dataGridSx}
                components={{
                  NoRowsOverlay: () => (
                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                      <Typography variant="body2" color="text.secondary">
                        No sub categories found
                      </Typography>
                    </Box>
                  ),
                }}
              />
            </Box>
          )}

          <AddSubCategoryModal
            open={modalOpen}
            onClose={handleModalClose}
            refetch={refetch}
            selectedRow={selectedRow}
          />
        </Box>
      </Box>
    </Container>
  );
}
